import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { TimerStopwatch } from '@amzn/katal-metrics/lib/metricObject'
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { FlyoutContent, WithFlyout } from '@amzn/stencil-react-components/flyout'
import { Checkbox, Input, InputWrapper, LabelPosition } from '@amzn/stencil-react-components/form'
import { IconSearch, IconSettings } from '@amzn/stencil-react-components/icons'
import { Col, Container, Flex, Hr, View } from '@amzn/stencil-react-components/layout'
import { MessageBanner, MessageBannerType } from '@amzn/stencil-react-components/message-banner'
import { useBreakpoints } from '@amzn/stencil-react-components/responsive'
import { Spinner, SpinnerSize } from '@amzn/stencil-react-components/spinner'
import { SortDirection, TableColumn } from '@amzn/stencil-react-components/table'
import { H2, H4, Label } from '@amzn/stencil-react-components/text'

import { ModuleDisplayTable } from 'src/components/ModuleDisplayTable'
import { FILTER_DATA_IDS, ModuleListFilter } from 'src/components/ModuleListFilter'
import { ResponsiveRow } from 'src/components/ResponsiveRow'
import {
    CommonUserEventKeys,
    publishTimeSpentMetrics,
    publishUserEventMetrics,
    UserEventMethodNames,
} from 'src/metrics'
import { ModuleType, QueryParams } from 'src/models/dto/ModuleDTO'
import { ModuleStatus } from 'src/models/dto/ModuleStatus'
import {
    ModuleListFilters,
    ModuleListSortCriteria,
    PaginationConfig,
} from 'src/models/ListModulesRequest'
import { ModuleMetaData, SearchResultResponse } from 'src/models/SearchResultResponse'
import { SearchService } from 'src/services/backend/SearchService'

const DEFAULT_PAGE_SIZE = 10

export interface ModuleSearchQuery {
    search: string
    owner: string
    startDate: string
    endDate: string
    lastModifiedBy: string
    searchArchived: boolean
    moduleStatus: ModuleStatus[]
    moduleTypes: ModuleType[]
    itemOffset: number
    numberOfItemsPerPage: number
    sortField: string
    sortDirection: SortDirection
}

export enum LAYOUT_DIRECTION {
    DESKTOP = 'desktop',
    TABLET = 'tablet',
    MOBILE = 'mobile',
}

export const useLayoutDirection = () => {
    const { matches } = useBreakpoints()

    if (matches.s) {
        return LAYOUT_DIRECTION.MOBILE
    }

    if (matches.m) {
        return LAYOUT_DIRECTION.TABLET
    }

    return LAYOUT_DIRECTION.DESKTOP
}

const pageTimer = new TimerStopwatch('SearchResultPageTimer')

const getSortCriteriaFromParams = (urlSearchParams: URLSearchParams) => {
    const sortField = urlSearchParams.get(QueryParams.SORT_FIELD) ?? 'modifiedAt'
    const sortDirection =
        urlSearchParams.get(QueryParams.SORT_DIRECTION) === SortDirection.Ascending.toString()
            ? SortDirection.Ascending
            : SortDirection.Descending
    return { sortField, sortDirection }
}

const convertParams = (searchParams: URLSearchParams): ModuleSearchQuery => {
    const sortCriteriaFromParams = getSortCriteriaFromParams(searchParams)
    return {
        search: searchParams.get(QueryParams.SEARCH) ?? '',
        owner: searchParams.get(QueryParams.OWNER) ?? '',
        startDate: searchParams.get(QueryParams.START_DATE) ?? '',
        endDate: searchParams.get(QueryParams.END_DATE) ?? '',
        lastModifiedBy: searchParams.get(QueryParams.LAST_MODIFIED_BY) ?? '',
        searchArchived: searchParams.get(QueryParams.ARCHIVED) === 'true',
        moduleStatus: (searchParams.getAll(QueryParams.STATUS) as ModuleStatus[]) ?? [],
        moduleTypes: (searchParams.getAll(QueryParams.TYPE) as ModuleType[]) ?? [],
        itemOffset: +(searchParams.get(QueryParams.OFFSET) ?? 0),
        numberOfItemsPerPage: +(
            searchParams.get(QueryParams.RECORDS_PER_PAGE) ?? DEFAULT_PAGE_SIZE
        ),
        sortField: sortCriteriaFromParams.sortField,
        sortDirection: sortCriteriaFromParams.sortDirection,
    }
}

export const SearchResultsPage = ({
    embeddedInModal: embedded,
    lockedModuleStatus,
    selectModuleAction,
    disabledFilters,
    lockedModuleTypes,
    noTypeColumn,
}: {
    embeddedInModal?: boolean
    lockedModuleStatus?: ModuleStatus[]
    selectModuleAction?: TableColumn<ModuleMetaData>
    disabledFilters?: FILTER_DATA_IDS[]
    lockedModuleTypes?: ModuleType[]
    noTypeColumn?: boolean
}) => {
    const [urlSearchParams, setUrlSearchParams] = useSearchParams()
    const [reactStateSearchParams, setReactStateSearchParams] = useState<URLSearchParams>(
        () => new URLSearchParams()
    )

    // If it's embedded in a modal, we use local state to switch between searches, otherwise we use URL query parameters
    const searchParams = embedded ? reactStateSearchParams : urlSearchParams
    const setSearchParams: React.Dispatch<URLSearchParams> = embedded
        ? setReactStateSearchParams
        : setUrlSearchParams

    const [nextSearchQuery, setNextSearchQuery] = useState<ModuleSearchQuery>(() => {
        if (embedded) {
            return {
                search: '',
                owner: '',
                startDate: '',
                endDate: '',
                lastModifiedBy: '',
                searchArchived: false,
                moduleStatus: lockedModuleStatus ?? [],
                moduleTypes: lockedModuleTypes ?? [],
                itemOffset: 0,
                numberOfItemsPerPage: DEFAULT_PAGE_SIZE,
                sortField: 'modifiedAt',
                sortDirection: SortDirection.Descending,
            }
        }
        return convertParams(searchParams)
    })

    const {
        setSearch,
        setOwner,
        setStartDate,
        setEndDate,
        setLastModifiedBy,
        setSearchArchived,
        setModuleStatus,
        setModuleTypes,
        setItemOffset,
        setNumberOfItemsPerPage,
        setSortCriteria,
    } = useMemo(
        () => ({
            setSearch: (value: string) =>
                setNextSearchQuery((prev) => ({ ...prev, search: value })),
            setOwner: (value: string) => setNextSearchQuery((prev) => ({ ...prev, owner: value })),
            setStartDate: (value: string) =>
                setNextSearchQuery((prev) => ({ ...prev, startDate: value })),
            setEndDate: (value: string) =>
                setNextSearchQuery((prev) => ({ ...prev, endDate: value })),
            setLastModifiedBy: (value: string) =>
                setNextSearchQuery((prev) => ({ ...prev, lastModifiedBy: value })),
            setSearchArchived: (value: boolean) =>
                setNextSearchQuery((prev) => ({ ...prev, searchArchived: value })),
            setModuleStatus: (value: ModuleStatus[]) =>
                setNextSearchQuery((prev) => ({ ...prev, moduleStatus: value })),
            setModuleTypes: (value: ModuleType[]) =>
                setNextSearchQuery((prev) => ({ ...prev, moduleTypes: value })),
            setItemOffset: (value: number) =>
                setNextSearchQuery((prev) => ({ ...prev, itemOffset: value })),
            setNumberOfItemsPerPage: (value: number) =>
                setNextSearchQuery((prev) => ({ ...prev, numberOfItemsPerPage: value })),
            setSortCriteria: (value: ModuleListSortCriteria) =>
                setNextSearchQuery((prev) => ({
                    ...prev,
                    sortField: value.sortField,
                    sortDirection: value.sortDirection,
                })),
        }),
        []
    )

    const [loadingResults, setLoadingResults] = useState<boolean>(false)
    const [searchResults, setSearchResults] = useState<SearchResultResponse>(
        {} as SearchResultResponse
    )
    const layout = useLayoutDirection()
    const [hasLoadedResults, setHasLoadedResults] = useState(false)

    const isMounted = useRef(true)
    useEffect(
        () => () => {
            isMounted.current = false
        },
        []
    )
    const callSearchAPI = useCallback(async () => {
        setLoadingResults(true)
        const filters: ModuleListFilters = {
            owner: nextSearchQuery.owner,
            lastModifiedBy: nextSearchQuery.lastModifiedBy,
            startDate: nextSearchQuery.startDate,
            endDate: nextSearchQuery.endDate,
            moduleTypes: nextSearchQuery.moduleTypes,
            moduleStatuses: nextSearchQuery.moduleStatus,
        }

        const pageConfig: PaginationConfig = {
            offset: nextSearchQuery.itemOffset,
            numberOfRecordsPerPage: nextSearchQuery.numberOfItemsPerPage,
        }

        const sortCriteria: ModuleListSortCriteria = {
            sortField: nextSearchQuery.sortField,
            sortDirection: nextSearchQuery.sortDirection,
        }

        try {
            const response: SearchResultResponse = await SearchService.searchModules(
                nextSearchQuery.search,
                filters,
                pageConfig,
                sortCriteria,
                undefined,
                nextSearchQuery.searchArchived
            )

            if (!isMounted.current) {
                return
            }
            setSearchParams(
                SearchService.constructQueryParams(
                    nextSearchQuery.search,
                    nextSearchQuery.searchArchived,
                    filters,
                    pageConfig,
                    sortCriteria,
                    true
                )
            )
            setSearchResults(response)
        } finally {
            if (isMounted.current) {
                setLoadingResults(false)
            }
        }
    }, [nextSearchQuery, setSearchParams])

    // When search params change in URL, write back
    useEffect(() => {
        if (embedded) return

        setNextSearchQuery(convertParams(searchParams))
    }, [embedded, searchParams])

    const resultTableRef = useRef<HTMLDivElement | null>(null)

    const searchThenFocus = useCallback(() => {
        setHasLoadedResults(false)
        requestAnimationFrame(() => resultTableRef?.current?.focus())
    }, [resultTableRef])

    const handleSortCriteriaChange = useCallback(
        (
            sortField: 'status' | 'moduleName' | 'moduleType' | 'modifiedAt',
            sortDirection: SortDirection,
            offset: number,
            numberOfRecordsPerPage: number
        ) => {
            setSortCriteria({ sortField, sortDirection })
            setItemOffset(offset)
            setNumberOfItemsPerPage(numberOfRecordsPerPage)
            setHasLoadedResults(false)
        },
        [setSortCriteria, setItemOffset, setNumberOfItemsPerPage]
    )

    const containerRef = useRef<HTMLDivElement | null>(null)
    useEffect(() => {
        containerRef.current?.focus()
    }, [])

    useEffect(() => {
        pageTimer.start()
        publishUserEventMetrics(UserEventMethodNames.SearchResult, CommonUserEventKeys.Open)

        return () => {
            pageTimer.stop()
            publishTimeSpentMetrics(UserEventMethodNames.SearchResult, pageTimer.value)
        }
    }, [])

    // Perform search
    useEffect(() => {
        if (!hasLoadedResults && !loadingResults) {
            ;(async function () {
                await callSearchAPI()
                if (isMounted.current) {
                    setHasLoadedResults(true)
                }
            })().catch(console.error)
        }
    }, [callSearchAPI, hasLoadedResults, loadingResults])

    const renderFlyout = useCallback(
        ({ close }: { close: () => void }) => (
            <FlyoutContent titleText='Filters' onCloseButtonClick={close}>
                <ModuleListFilter
                    owner={nextSearchQuery.owner}
                    lastModifiedBy={nextSearchQuery.lastModifiedBy}
                    startDate={nextSearchQuery.startDate}
                    endDate={nextSearchQuery.endDate}
                    moduleTypes={nextSearchQuery.moduleTypes}
                    moduleStatus={nextSearchQuery.moduleStatus}
                    setOwner={setOwner}
                    setLastModifiedBy={setLastModifiedBy}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    setModuleTypes={setModuleTypes}
                    setModuleStatus={setModuleStatus}
                    lockedModuleStatus={lockedModuleStatus}
                    applyFilters={() => {
                        setItemOffset(0)
                        setHasLoadedResults(false)
                        close()
                    }}
                />
            </FlyoutContent>
        ),
        [
            nextSearchQuery.owner,
            nextSearchQuery.lastModifiedBy,
            nextSearchQuery.startDate,
            nextSearchQuery.endDate,
            nextSearchQuery.moduleTypes,
            nextSearchQuery.moduleStatus,
            setOwner,
            setLastModifiedBy,
            setStartDate,
            setEndDate,
            setModuleTypes,
            setModuleStatus,
            setItemOffset,
            lockedModuleStatus,
        ]
    )

    const onPageChange = useCallback(
        (offset: number, numberOfRecordsPerPage: number) => {
            setItemOffset(offset)
            setNumberOfItemsPerPage(numberOfRecordsPerPage)
            setHasLoadedResults(false)
        },
        [setItemOffset, setNumberOfItemsPerPage]
    )

    const sortCriteria = useMemo(
        () => ({
            sortField: nextSearchQuery.sortField,
            sortDirection: nextSearchQuery.sortDirection,
        }),
        [nextSearchQuery.sortDirection, nextSearchQuery.sortField]
    )

    const noResults = (searchResults?.data?.length ?? 0) === 0
    const displaySearchResults = useMemo(() => {
        if (loadingResults) {
            return (
                <View display='flex' justifyContent='center' width='100%' alignItems='center'>
                    <Spinner size={SpinnerSize.Large} dataTestId={'module-search-data-spinner'} />
                </View>
            )
        }

        // No search results
        if (noResults) {
            return (
                <View display='flex' justifyContent='center' width='100%' alignItems='center'>
                    <H4 color='neutral70' textAlign='center' data-no-search-results>
                        {`No results found for "${nextSearchQuery.search}"`} <br />{' '}
                        {'Please search again.'}
                    </H4>
                </View>
            )
        }

        // Display search results
        return (
            <View tabIndex={-1} ref={resultTableRef}>
                <ModuleDisplayTable
                    searchResults={searchResults}
                    searchTerm={nextSearchQuery.search}
                    offset={nextSearchQuery.itemOffset}
                    onUpdateNumberOfItemsPerPage={setNumberOfItemsPerPage}
                    onPageChange={onPageChange}
                    onSortCriteriaChange={handleSortCriteriaChange}
                    sortCriteria={sortCriteria}
                    embeddedInModal={embedded}
                    selectModuleAction={selectModuleAction}
                    noTypeColumn={noTypeColumn}
                />
            </View>
        )
    }, [
        embedded,
        handleSortCriteriaChange,
        loadingResults,
        nextSearchQuery.itemOffset,
        nextSearchQuery.search,
        noResults,
        noTypeColumn,
        onPageChange,
        searchResults,
        selectModuleAction,
        setNumberOfItemsPerPage,
        sortCriteria,
    ])

    const applyFilters = useCallback(() => {
        setItemOffset(0)
        setHasLoadedResults(false)
    }, [setItemOffset])

    const onSearchArchivedChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setItemOffset(0)
            setSearchArchived(e.target.checked)
            setHasLoadedResults(false)
        },
        [setItemOffset, setSearchArchived]
    )

    return (
        <Container
            ref={containerRef}
            backgroundColor='#F8F8F8'
            width='100%'
            minHeight='100vh'
            paddingHorizontal={0}
        >
            <Col gridGap='S300' padding='15px'>
                <Label htmlFor='module-search-input'>
                    <H2>Search</H2>
                </Label>
                <Flex
                    gridGap='16px'
                    flexDirection={layout === LAYOUT_DIRECTION.MOBILE ? 'column' : 'row'}
                >
                    {useMemo(
                        () => (
                            <Input
                                id='module-search-input'
                                key={'module-search-input'}
                                data-test-id='module-search-input'
                                name='Module Search Input'
                                width={layout === LAYOUT_DIRECTION.MOBILE ? '100%' : '50%'}
                                defaultValue={nextSearchQuery.search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        setItemOffset(0)
                                        setHasLoadedResults(false)
                                    }
                                }}
                                insetElementLeading={{
                                    element: <IconSearch aria-hidden={true} color='neutral60' />,
                                }}
                            />
                        ),
                        [layout, nextSearchQuery.search, setItemOffset, setSearch]
                    )}
                    <Button
                        variant={ButtonVariant.Primary}
                        onClick={() => {
                            setItemOffset(0)
                            searchThenFocus()
                        }}
                        aria-label='Search'
                        data-test-id='search-button'
                    >
                        Search
                    </Button>
                </Flex>
                <ResponsiveRow>
                    {useMemo(
                        () => (
                            <InputWrapper
                                id='searched-archived-input'
                                labelText={'Search archived modules'}
                                labelPosition={LabelPosition.Trailing}
                            >
                                {(inputProps) => (
                                    <Checkbox
                                        {...inputProps}
                                        checked={nextSearchQuery.searchArchived}
                                        onChange={onSearchArchivedChange}
                                    />
                                )}
                            </InputWrapper>
                        ),
                        [nextSearchQuery.searchArchived, onSearchArchivedChange]
                    )}
                </ResponsiveRow>
                <Hr size='narrow' color='#B9C0C8' />
                {nextSearchQuery.searchArchived && (
                    <MessageBanner type={MessageBannerType.Informational}>
                        You are searching archived modules. Only archived modules will appear.
                    </MessageBanner>
                )}
                <Flex
                    gridGap='16px'
                    flexDirection={layout === LAYOUT_DIRECTION.MOBILE ? 'column' : 'row'}
                >
                    {useMemo(
                        () =>
                            layout === LAYOUT_DIRECTION.MOBILE ? (
                                <WithFlyout renderFlyout={renderFlyout}>
                                    {({ open }) => (
                                        <Button
                                            variant={ButtonVariant.Secondary}
                                            icon={<IconSettings aria-hidden={true} />}
                                            onClick={open}
                                        >
                                            Filters
                                        </Button>
                                    )}
                                </WithFlyout>
                            ) : (
                                <Col width='30%'>
                                    <ModuleListFilter
                                        title={'Filters'}
                                        owner={nextSearchQuery.owner}
                                        lastModifiedBy={nextSearchQuery.lastModifiedBy}
                                        startDate={nextSearchQuery.startDate}
                                        endDate={nextSearchQuery.endDate}
                                        moduleTypes={nextSearchQuery.moduleTypes}
                                        moduleStatus={nextSearchQuery.moduleStatus}
                                        setOwner={setOwner}
                                        setLastModifiedBy={setLastModifiedBy}
                                        setStartDate={setStartDate}
                                        setEndDate={setEndDate}
                                        setModuleTypes={setModuleTypes}
                                        setModuleStatus={setModuleStatus}
                                        applyFilters={applyFilters}
                                        lockedModuleStatus={lockedModuleStatus}
                                        disableFilters={disabledFilters}
                                    />
                                </Col>
                            ),
                        [
                            applyFilters,
                            disabledFilters,
                            layout,
                            lockedModuleStatus,
                            nextSearchQuery.endDate,
                            nextSearchQuery.lastModifiedBy,
                            nextSearchQuery.moduleStatus,
                            nextSearchQuery.moduleTypes,
                            nextSearchQuery.owner,
                            nextSearchQuery.startDate,
                            renderFlyout,
                            setEndDate,
                            setLastModifiedBy,
                            setModuleStatus,
                            setModuleTypes,
                            setOwner,
                            setStartDate,
                        ]
                    )}
                    {displaySearchResults}
                </Flex>
            </Col>
        </Container>
    )
}
